import React, { useEffect, useState } from 'react';
import { useMount } from 'react-use';
import './DashboardFilters.scss';
import { TDimension } from '../../../api/analytics/Dimension';
import AddFilterModal from './AddFilterModal';
import { DashboardFilter, TDashboardFilter } from '../../../api/dashbboards/DashboardFilter';
import { AlertVariant, Button, Form, FormGroup, Tooltip } from '@patternfly/react-core';
import DatePeriodSelector from '../../../components/date-period-selector/DatePeriodSelector';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faPlus } from '@fortawesome/pro-light-svg-icons';
import Loader from '../../../components/util/Loader';
import EditFilterModal, { EditDashboardFilter } from './EditFilterModal';
import { TNewDateRange } from '../../../api/types/TNewDateRange';
import DashboardFilterDropdown from './DashboardFilter';
import { useToast } from '@zeroedin-tech/zi-common-ui/lib';
import { OptionsBuilderItemTypes } from '../../../types/dataframes/options-builder-item-types';
import { useApplication } from '../../../components/user/ApplicationProvider';
import { faFilter } from '@fortawesome/pro-solid-svg-icons';
import { faTimes } from '@fortawesome/pro-regular-svg-icons';
import DashboardFiltersHorizontalGrid from './DashboardFiltersHorizontalGrid';

type Props = {
	dashboardId?: number;
	presentationId?: number;
	setSelectedDate: (startDateRange: TNewDateRange, endDateRange: TNewDateRange) => void;
	setUnSavedChanges?: React.Dispatch<React.SetStateAction<boolean>>;
	updateFiltersToDelete?: (filter: DashboardFilter) => void;
	updateFilters: (filters: DashboardFilter[]) => void;
	setDashboardFiltersToDelete?: React.Dispatch<
		React.SetStateAction<DashboardFilter[] | undefined>
	>;
	setIsEmptyDashboardFilters?: React.Dispatch<React.SetStateAction<boolean | undefined>>;
	isView?: boolean;
	dashboardFiltersTemp?: DashboardFilter[];
	setDashboardFiltersTemp?: React.Dispatch<React.SetStateAction<DashboardFilter[] | undefined>>;
};

const DashboardFilters = (props: Props) => {
	const {
		dashboardId,
		presentationId,
		setSelectedDate,
		setUnSavedChanges,
		updateFiltersToDelete,
		updateFilters,
		isView,
	} = props;
	const [isLoading, setIsLoading] = useState<boolean>(false);
	const { dimensions } = useApplication();
	const [allSeries, setAllSeries] = useState<TDimension[]>(dimensions);
	const [isAddFilterModalOpen, setIsAddFilterModalOpen] = useState<boolean>(false);
	const [isEditFilterModalOpen, setIsEditFilterModalOpen] = useState<boolean>(false);
	const [dashboardFilters, setDashboardFilters] = useState<DashboardFilter[]>([]);
	const [currentEditDashboardFilter, setCurrentEditDashboardFilter] =
		useState<EditDashboardFilter>({
			name: '',
			filter: DashboardFilter.Default(),
		});
	const [viewOnlyFilters, setViewOnlyFilters] = useState<number[]>([]);
	const { addToast } = useToast();

	useMount(() => {
		setIsLoading(true);
		initFilterRequest();

		const mappedDimensions = allSeries.map((x) => ({
			...x,
			type: OptionsBuilderItemTypes.Dimension,
			dimensionAttributes: x.dimensionAttributes.map((y) => ({
				...y,
				type: OptionsBuilderItemTypes.DimensionAttribute,
			})),
		}));

		setAllSeries(mappedDimensions);
	});

	useEffect(() => {
		initFilterRequest();
	}, [presentationId]);

	const initFilterRequest = () => {
		let filterRequest = undefined;
		if (dashboardId) {
			filterRequest = { dashboard: dashboardId.toString() };
		} else if (presentationId) {
			filterRequest = { presentation: presentationId.toString() };
		}
		if (filterRequest) {
			DashboardFilter.GetAll(filterRequest)
				.then((dashboardFilters) => {
					setDashboardFilters(dashboardFilters);
					props.setDashboardFiltersTemp &&
						props.setDashboardFiltersTemp(dashboardFilters);
					props.setIsEmptyDashboardFilters &&
						props.setIsEmptyDashboardFilters(dashboardFilters.length == 0);
					updateFilters(dashboardFilters);
					if (isView) {
						setViewOnlyFilters(
							dashboardFilters
								.filter((df) => df.value === undefined || df.value === '')
								.map((df) => df.id ?? 0)
						);
					}
				})
				.catch((): void => {
					addToast('Error loading filters.', AlertVariant.danger);
				})
				.finally(() => {
					setIsLoading(false);
				});
		} else {
			setDashboardFilters([]);
			updateFilters([]);
			setIsLoading(false);
		}
	};

	const handleFilterModalOpen = (dashboardFilterId: number, entityId: number) => {
		let name = '';
		const current = dashboardFilters.find((df) => df.id === dashboardFilterId);

		if (current) {
			if (current.entity_type === OptionsBuilderItemTypes.Dimension) {
				const dim = allSeries.find((s) => s.id === entityId);
				name = dim?.name ?? '';
			} else if (current.entity_type === OptionsBuilderItemTypes.DimensionAttribute) {
				const dim = allSeries.find((s) =>
					s.dimensionAttributes.some(
						(attr) => attr.id === entityId && attr.type === current.entity_type
					)
				);
				const dimAttr = dim?.dimensionAttributes.find(
					(attr) => attr.id === entityId && attr.type === current.entity_type
				);
				name = dimAttr?.name ?? '';
			}

			setCurrentEditDashboardFilter({
				name: name,
				filter: current,
			});
			setIsEditFilterModalOpen(true);
		}
	};

	const allowEdit = (dashboardFilterId: number) => {
		const current = dashboardFilters.find((df) => df.id === dashboardFilterId);
		return !isView || (isView && (current?.value === undefined || current?.value === ''));
	};

	const getDashboardFilter = (dashboardFilter: DashboardFilter) => {
		let name = '';
		const current = dashboardFilters.find((df) => df.id === dashboardFilter.id);

		if (dashboardFilter.entity_type === OptionsBuilderItemTypes.Dimension) {
			const dim = allSeries.find((s) => s.id === dashboardFilter.entity_id);
			name = dim?.name ?? '';
		} else if (dashboardFilter.entity_type === OptionsBuilderItemTypes.DimensionAttribute) {
			const dim = allSeries.find((s) =>
				s.dimensionAttributes.some(
					(attr) =>
						attr.id === dashboardFilter.entity_id &&
						attr.type === dashboardFilter.entity_type
				)
			);

			const dimAttr = dim?.dimensionAttributes.find(
				(attr) =>
					attr.id === dashboardFilter.entity_id &&
					attr.type === dashboardFilter.entity_type
			);
			name = dimAttr?.name ?? '';
		}

		return (
			<DashboardFilterDropdown
				id={dashboardFilter.id ?? 0}
				entityId={dashboardFilter.entity_id}
				handleFilterModalOpen={handleFilterModalOpen}
				handleRemoveDashboardFilter={handleRemoveDashboardFilter}
				name={name}
				isView={isView}
				editable={allowEdit(dashboardFilter.id ?? 0)}
				isFiltered={current?.value && current.value !== '' ? true : false}
			/>
		);
	};

	const handleSaveDashboardFilter = (dashboardFilter: TDashboardFilter) => {
		if (!isView && allowEdit(dashboardFilter.id ?? 0)) {
			setUnSavedChanges && setUnSavedChanges(true);
		}

		// Replace the filter if it exists, or add it
		const newFilters = dashboardFilters.map((filter) =>
			filter.id === dashboardFilter.id ? { ...dashboardFilter } : filter
		);

		setDashboardFilters(newFilters);
		updateFilters(newFilters);
	};

	const handleAddDashboardFilter = (selectedDimId: number, entityType: string) => {
		setUnSavedChanges && setUnSavedChanges(true);

		const dashboardFilterId = dashboardFilters.find(
			(d) => d.id === dashboardFilters.length + 1
		)?.id;

		const newModel = DashboardFilter.Default();
		newModel.dashboard = dashboardId;
		newModel.id = dashboardFilterId ? dashboardFilterId + 1 : dashboardFilters.length + 1; //A temp id assigned to a filter that will not be saved to the DB
		newModel.entity_id = selectedDimId;
		newModel.entity_type = entityType;
		newModel.isNew = true;
		newModel.sequence = dashboardFilters.length;

		const updatedFilters = [...dashboardFilters, newModel];

		setDashboardFilters(updatedFilters);
		updateFilters(updatedFilters);
	};

	const handleRemoveDashboardFilter = (id: number) => {
		setUnSavedChanges && setUnSavedChanges(true);

		const filterToDelete = dashboardFilters.find((filter) => filter.id === id);

		if (filterToDelete) {
			updateFiltersToDelete && updateFiltersToDelete(filterToDelete);

			const updatedFilters = dashboardFilters
				.filter((df) => df.id !== id)
				.sort((a, b) => a.sequence - b.sequence)
				.map((filter, index) => ({
					...filter,
					sequence: index,
				}));

			updateFilters(updatedFilters);
			setDashboardFilters(updatedFilters);
			props.setDashboardFiltersTemp && props.setDashboardFiltersTemp(updatedFilters);
		}
	};

	const handleRemoveAllDashboardFilters = () => {
		setUnSavedChanges && setUnSavedChanges(true);

		// Clone rather than mutate: these objects are shared with DashboardView's
		// state, and mutating them in place makes its before/after filter diff
		// compare the same values, so "clear all" would never record a change.
		const newFilters = dashboardFilters
			.map((filter) => {
				const readonly = isView && !viewOnlyFilters.find((i) => i === filter.id);
				return readonly ? filter : { ...filter, value: '' };
			})
			.sort((a, b) => a.sequence - b.sequence);

		setDashboardFilters(newFilters);
		updateFilters(newFilters);
	};

	return (
		<>
			<Form
				isHorizontal
				className="dashboard-form"
			>
				<FormGroup
					label={
						<div className="formgroup-label">
							Filters
							<FontAwesomeIcon icon={faFilter} />
							{dashboardFilters.some(
								(f, index) =>
									(!isView && Object.hasOwn(f, 'value') && f.value !== '') ||
									(isView &&
										viewOnlyFilters.find((i) => i === f.id) &&
										Object.hasOwn(f, 'value') &&
										f.value !== '')
							) && (
								<div
									className="remove-btn"
									style={{
										marginLeft: '-15px',
									}}
								>
									<Tooltip content="Clear All Filters">
										<Button
											variant="plain"
											alt="Clear All Filters"
											icon={
												<FontAwesomeIcon
													icon={faTimes}
													onClick={() =>
														handleRemoveAllDashboardFilters()
													}
												/>
											}
										></Button>
									</Tooltip>
								</div>
							)}
						</div>
					}
					isInline
				>
					<div className="filter-container">
						<div className="date-selector-container">
							<DatePeriodSelector applyDateRange={setSelectedDate} />
						</div>
						{isLoading ? (
							<div className="filter">
								<Loader size="md" />
							</div>
						) : (
							<DashboardFiltersHorizontalGrid
								allSeries={allSeries}
								allowEdit={allowEdit}
								dashboardFilters={dashboardFilters}
								handleFilterModalOpen={handleFilterModalOpen}
								handleRemoveDashboardFilter={handleRemoveDashboardFilter}
								isView={isView ?? false}
								setDashboardFilters={setDashboardFilters}
								setDashboardFiltersTemp={props.setDashboardFiltersTemp}
							/>
						)}
						{!isView && (
							<div className="add-filter">
								<Tooltip content="Add Filter">
									<Button
										className="btn-add-filter no-color-override"
										alt="Add Filter"
										icon={<FontAwesomeIcon icon={faPlus} />}
										onClick={() => {
											setIsAddFilterModalOpen(true);
										}}
									>
										<strong>Add Filter</strong>
									</Button>
								</Tooltip>
							</div>
						)}
					</div>
				</FormGroup>
			</Form>
			{isAddFilterModalOpen && (
				<AddFilterModal
					setModalOpen={setIsAddFilterModalOpen}
					isOpen={isAddFilterModalOpen}
					addDashboardFilter={handleAddDashboardFilter}
					selectedIds={dashboardFilters.map((f) => f.entity_id)}
				/>
			)}

			<EditFilterModal
				dashboardFilter={currentEditDashboardFilter}
				setModalOpen={setIsEditFilterModalOpen}
				isOpen={isEditFilterModalOpen}
				saveDashboardFilter={handleSaveDashboardFilter}
				readonly={
					!viewOnlyFilters.find((i) => i === currentEditDashboardFilter.filter.id) &&
					!allowEdit(currentEditDashboardFilter.filter.id ?? 0)
				}
			/>
		</>
	);
};

export default DashboardFilters;
